/* eslint-disable react/prop-types */
import { Box } from '@eatclubcode/ec-component-library';
import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import Amplify, { API, Auth } from 'aws-amplify';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Backdrop } from '@mui/material';
import AppUpdateBar from './components/AppUpdateBar/AppUpdateBar';
import { obeeSettings } from './data/graphql/queries';
import AppLayout from './layouts/AppLayout';
import Body from './components/Layout/body/Body';
import Routes from './routes/SwitchRoutes'; // Must be called Routes or the router breaks
import useStyles from './AppStyles';
import obeeConfig from './obee-aws-exports';
import { fetchUserAction, setLogoutAction } from './data/actions/loginAction';
import { setActiveRestaurantAction } from './data/actions/restaurantActiveAction';
import { fetchLinkedVenuesAction, fetchRestaurantAction } from './data/actions/restaurantAction';
import Error from './components/Error/Error';
import './App.css';
import './DatePickerStyles.css';
import { devLog } from './utils';
import { getAppVersion, isEmpty } from './utils/helpers';
import { useInterval } from './utils/hooks';

Amplify.configure(obeeConfig);

// NOTE: enable for debugging
// Amplify.Logger.LOG_LEVEL = "DEBUG";
// const currentConfig = Auth.configure();
// console.log("currentConfig: ", currentConfig);

const App = ({
  fetchRestaurant,
  fetchUser,
  restaurant,
  restaurantActive,
  setActiveRestaurant,
  user,
  fetchLinkedVenues,
  setLogout,
}) => {
  const location = useLocation();
  const classes = useStyles();

  const { id: userId } = user.userInfo;
  const [authSignedIn, setAuthSignedIn] = useState(false); // TODO fix this, shouldn't need state
  const [defaultView, setDefaultView] = useState(null);

  useEffect(() => {
    if (user?.userInfo?.id && import.meta.env.VITE_GOOGLE_ANALYTICS_MEASUREMENT_ID) {
      try {
        /* Initialize analytics & load plugins */
        ReactGA.initialize(import.meta.env.VITE_GOOGLE_ANALYTICS_MEASUREMENT_ID, {
          gaOptions: {
            userId: user?.userinfo?.objectId,
          },
        });
      } catch (e) {
        devLog('warn', 'Failed to initialize Google Analytics');
      }
    }
  }, [user?.userInfo?.id]);

  // Fetch User Object
  useEffect(() => {
    // Extract objectId from the User in the User Pool
    Auth.currentAuthenticatedUser()
      .then((response) => {
        if (!response) {
          return null;
        }

        const authUserId = response.attributes['custom:objectId'];
        setAuthSignedIn(authUserId);

        if (authUserId) {
          // Fetch the User from the User's table using the objectId
          fetchUser(authUserId, 'partner'); // Probably don't want to send 'obee' as user type??

          // if (userType === 'obee' && history.location.pathname === '/obeeLogin') {
          //   window.location.href = `${window.location.origin}?${params.toString()}`;
          // }
          return null;
        }

        if (!sessionStorage.getItem('selectedRestaurant')) {
          sessionStorage.setItem('selectedRestaurant', authUserId ?? userId);
          localStorage.setItem(`lastUsedRestaurant`, authUserId ?? userId);
        }

        devLog('error', 'User objectId is not set!');
        return null;
      })
      .catch((error) => {
        devLog('error', 'User is not signed in', error);
      });
  }, [fetchUser]);

  // Fetch Restaurant
  useEffect(() => {
    if (user.userFetching || user.userError) {
      return;
    }

    const restaurantId = sessionStorage.getItem('selectedRestaurant') || authSignedIn;

    const ssoToken = sessionStorage.getItem('ssoToken') || user?.userInfo?.ssoToken;
    // Switch to default screen if not on a specific screen
    if (location.pathname === '/' || location.pathname === '/index.html') {
      API.graphql(
        {
          query: obeeSettings,
          variables: { restId: restaurantId },
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        },
        {
          'user-id': userId,
          'user-type': user.userInfo.userType,
          'rest-id': restaurantId,
          'app-version': getAppVersion(),
          'X-App-Environment': import.meta.env?.VITE_API_ENVIRONMENT,
        },
      ).then((response) => {
        // Redirect to the restaurant's preferred page
        window.location.href = response?.data?.obeeSettings?.defaultLandingPage || '/runsheet';
      });
    }

    // if (user.userInfo.userType === 'partner') {
    // const id = restId === '' ? user.userInfo.restaurants[0].objectId : restId;
    // fetchRestaurant(id, userId, user.userInfo.userType);

    // With the new login flow, the restId is stored in the 'custom:objectId' attribute
    fetchRestaurant(restaurantId, userId, 'partner');
    fetchLinkedVenues(authSignedIn, userId, 'partner', ssoToken);

    // const isStaff = user.userInfo.userType === 'staff';
    // const isStaff = false;
    // const restIdFromParams = params.get('restId');
    // const sessionStaffRestaurant = sessionStorage.getItem('staffRestaurant');
  }, [
    fetchRestaurant,
    restaurantActive.id,
    user.userError,
    user.userFetching,
    user.userInfo.restaurants,
  ]);

  const halfHourInMilliseconds = 1000 * 60 * 30;
  // Refetch restaurant in case data changed elsewhere.
  // In particular, if the restaurant was closed the previous day we need to know if it's reopened
  useInterval(() => {
    if (!authSignedIn || !userId) {
      return;
    }

    fetchRestaurant(
      sessionStorage.getItem('selectedRestaurant') || authSignedIn,
      userId,
      'partner',
    );
  }, halfHourInMilliseconds);

  // Set Active Restaurant
  useEffect(() => {
    if (restaurant.restaurantFetching || restaurant.restaurantError) {
      return;
    }

    setActiveRestaurant(restaurant.restaurant);
  }, [
    restaurant.restaurant,
    restaurant.restaurantError,
    restaurant.restaurantFetching,
    setActiveRestaurant,
  ]);

  useEffect(() => {
    const obeeUserLinkedVenues = user?.obeeUserLinkedVenues?.data?.loggedInUser;

    if (
      user?.obeeUserLinkedVenues?.success &&
      !user?.loggingOut &&
      !isEmpty(restaurantActive?.restaurant?.slug) &&
      isEmpty(obeeUserLinkedVenues)
    ) {
      setLogout(restaurantActive?.restaurant?.slug);
    }
  }, [user?.obeeUserLinkedVenues, restaurantActive?.restaurant?.slug]);

  // Poll for an app update
  const [hasAppUpdate, setHasAppUpdate] = useState(false);
  // useInterval(
  //   async () => {
  //     if (hasAppUpdate) {
  //       return;
  //     }
  //
  //     const response = await fetch(`${import.meta.env.VITE_URL_PREFIX}/versionInfo`);
  //
  //     if (!response) {
  //       return;
  //     }
  //
  //     const content = await response.json();
  //
  //     if (
  //       !isEmpty(content?.version) &&
  //       !isEmpty(getVersionNumber()) &&
  //       content?.version !== getVersionNumber()
  //     ) {
  //       devLog(
  //         'info',
  //         `A new app update is available. Current version: ${getVersionNumber()}, New version: ${
  //           content?.version
  //         }`,
  //       );
  //
  //       // setHasAppUpdate(true);
  //     }
  //   },
  //   halfHourInMilliseconds,
  //   true,
  // );

  const errorMessage =
    (user?.userError && user?.userErrorMessage) ||
    (restaurant?.restaurantError && restaurant?.restaurantErrorMessage);

  return (
    <Router>
      <Box className={classes.root}>
        <AppUpdateBar hasUpdate={hasAppUpdate} />
        <AppLayout>
          <Body style={{ position: 'relative' }}>
            <Box>
              <>{errorMessage && <Error error message={errorMessage} />}</>

              {restaurantActive?.restaurantFetching === true ? (
                <Backdrop open style={{ zIndex: 9999 }} />
              ) : (
                <Routes activeRestaurant={restaurantActive?.restaurant} defaultView={defaultView} />
              )}
            </Box>
          </Body>
        </AppLayout>
      </Box>
    </Router>
  );
};

const mapStateToProps = (state) => ({
  restaurant: state.restaurant,
  restaurantActive: state.restaurantActive,
  user: state.user,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchRestaurant: fetchRestaurantAction,
      fetchLinkedVenues: fetchLinkedVenuesAction,
      fetchUser: fetchUserAction,
      setActiveRestaurant: setActiveRestaurantAction,
      setLogout: setLogoutAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(App);
